/**
 * Workspace Registry — WESTEER Platform
 *
 * @version 1.0
 * @created 2026-05-20
 * @description Fuente unica de verdad para la definicion de workspaces.
 *
 * Consumido por:
 * - Dashboard (cards de navegacion)
 * - App.tsx (registro de rutas)
 * - Guards de acceso (permisos y modulos)
 *
 * @remarks
 * Este archivo reemplaza la triple nomenclatura anterior:
 * - DASHBOARD_MODULES (ProjectDashboard.tsx)
 * - WesteerModule (auth/types.ts)
 * - ModuleId (module-store/types.ts)
 *
 * Los tipos WesteerModule y ModuleId siguen existiendo como contratos
 * del backend y del store respectivamente. Este registry los conecta
 * con la capa de navegacion y producto.
 */

import type { WesteerModule } from '../auth/types';
import type {
  WorkspaceCategory,
  WorkspaceCategoryGroup,
  WorkspaceDefinition,
  WorkspaceId,
} from './types';

// ─── Category Groups ──────────────────────────────────────────────────────────

export const WORKSPACE_CATEGORY_GROUPS: WorkspaceCategoryGroup[] = [
  { id: 'territorial-core', label: 'Nucleo territorial' },
  { id: 'decision-intelligence', label: 'Inteligencia y decision' },
  { id: 'operational-management', label: 'Gestion y coordinacion' },
];

// ─── Workspace Definitions ────────────────────────────────────────────────────

export const WORKSPACE_REGISTRY: WorkspaceDefinition[] = [
  // ── Nucleo territorial ──────────────────────────────────────────
  {
    id: 'territory',
    title: 'Explorador Territorial',
    shortTitle: 'Territorio',
    description:
      'Consultar capas territoriales, navegar la red urbana y visualizar el proyecto en modo lectura.',
    route: '/workspaces/territory',
    status: 'shell',
    category: 'territorial-core',
    requiresProject: true,
    requiredModules: [],
    requiredPermissions: [],
    primaryActionLabel: 'Abrir explorador',
    disabledLabel: 'En construccion',
    order: 1,
  },
  {
    id: 'survey',
    title: 'Relevamiento Territorial',
    shortTitle: 'Relevamiento',
    description:
      'Procesar recorridos, imagenes y videos para producir evidencia territorial y datos candidatos.',
    route: '/workspaces/survey',
    status: 'shell',
    category: 'territorial-core',
    requiresProject: true,
    requiredModules: [],
    requiredPermissions: [],
    primaryActionLabel: 'Abrir relevamiento',
    disabledLabel: 'En preparacion',
    order: 2,
  },
  {
    id: 'inventory',
    title: 'Inventario Urbano',
    shortTitle: 'Inventario',
    description:
      'Editor territorial completo: red urbana, inventario de entidades, modulos tematicos y herramientas de mapa.',
    route: '/workspaces/inventory',
    status: 'active',
    category: 'territorial-core',
    requiresProject: true,
    requiredModules: [],
    requiredPermissions: [],
    primaryActionLabel: 'Abrir editor territorial',
    disabledLabel: 'Requiere proyecto activo',
    order: 3,
    featured: true,
  },

  // ── Inteligencia y decision ─────────────────────────────────────
  {
    id: 'analysis',
    title: 'Analisis para Decision',
    shortTitle: 'Analisis',
    description:
      'Interpretar datos territoriales, detectar deficits, comparar escalas y orientar decisiones.',
    route: '/workspaces/analysis',
    status: 'shell',
    category: 'decision-intelligence',
    requiresProject: true,
    requiredModules: ['urban-network', 'roadways', 'infrastructure'],
    requiredPermissions: [],
    primaryActionLabel: 'Abrir analisis',
    disabledLabel: 'Requiere modulos de red activos',
    order: 4,
  },
  {
    id: 'scenarios',
    title: 'Planificacion de Escenarios',
    shortTitle: 'Escenarios',
    description:
      'Construir y comparar hipotesis de intervencion, simulacion e impacto territorial u operativo.',
    route: '/workspaces/scenarios',
    status: 'shell',
    category: 'decision-intelligence',
    requiresProject: true,
    requiredModules: [],
    requiredPermissions: [],
    primaryActionLabel: 'Abrir escenarios',
    disabledLabel: 'En preparacion',
    order: 5,
  },
  {
    id: 'reports',
    title: 'Reportes',
    shortTitle: 'Reportes',
    description:
      'Generar mapas, tableros, informes y exportaciones tecnicas o ejecutivas del proyecto.',
    route: '/workspaces/reports',
    status: 'future',
    category: 'decision-intelligence',
    requiresProject: true,
    requiredModules: [],
    requiredPermissions: [],
    primaryActionLabel: 'Abrir reportes',
    disabledLabel: 'En preparacion',
    order: 6,
  },

  // ── Gestion y coordinacion ──────────────────────────────────────
  {
    id: 'operations',
    title: 'Gestion Operativa',
    shortTitle: 'Operaciones',
    description:
      'Gestionar servicios, recorridos, unidades, turnos, cuadrillas e incidencias del proyecto.',
    route: '/workspaces/operations',
    status: 'shell',
    category: 'operational-management',
    requiresProject: true,
    requiredModules: ['waste'],
    requiredPermissions: [],
    primaryActionLabel: 'Abrir operaciones',
    disabledLabel: 'Requiere modulo operativo activo',
    order: 7,
  },
  {
    id: 'collaboration',
    title: 'Coordinacion',
    shortTitle: 'Coordinacion',
    description: 'Organizar tareas, comentarios, seguimiento y decisiones vinculadas al proyecto.',
    route: '/workspaces/collaboration',
    status: 'future',
    category: 'operational-management',
    requiresProject: true,
    requiredModules: [],
    requiredPermissions: [],
    primaryActionLabel: 'Abrir coordinacion',
    disabledLabel: 'En preparacion',
    order: 8,
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Obtener un workspace por ID. Retorna undefined si no existe. */
export function getWorkspaceById(id: WorkspaceId): WorkspaceDefinition | undefined {
  return WORKSPACE_REGISTRY.find(w => w.id === id);
}

/** Obtener todos los workspaces con status 'active'. */
export function getActiveWorkspaces(): WorkspaceDefinition[] {
  return WORKSPACE_REGISTRY.filter(w => w.status === 'active');
}

/** Obtener workspaces navegables (active + shell, no future). */
export function getNavigableWorkspaces(): WorkspaceDefinition[] {
  return WORKSPACE_REGISTRY.filter(w => w.status !== 'future');
}

/** Obtener la ruta de un workspace por ID. */
export function getWorkspaceRoute(id: WorkspaceId): string {
  const ws = getWorkspaceById(id);
  return ws?.route ?? '/dashboard';
}

/**
 * Determinar si un workspace esta habilitado para un proyecto dado sus modulos activos.
 *
 * Reglas:
 * - requiredModules vacio = siempre habilitado (cuando hay proyecto).
 * - requiredModules con IDs = habilitado si el proyecto tiene AL MENOS UNO.
 */
export function isWorkspaceEnabled(
  workspace: WorkspaceDefinition,
  activeModules: WesteerModule[] | undefined
): boolean {
  if (workspace.requiredModules.length === 0) return true;
  if (!activeModules || activeModules.length === 0) return false;
  return workspace.requiredModules.some(m => activeModules.includes(m));
}

/**
 * Obtener los workspaces disponibles para un proyecto segun sus modulos activos.
 * Solo incluye workspaces navegables (active + shell) que esten habilitados.
 */
export function getProjectWorkspaces(
  activeModules: WesteerModule[] | undefined
): WorkspaceDefinition[] {
  return getNavigableWorkspaces().filter(w => isWorkspaceEnabled(w, activeModules));
}

/** Obtener workspaces agrupados por categoria, respetando el orden del registry. */
export function getWorkspacesByCategory(): Record<WorkspaceCategory, WorkspaceDefinition[]> {
  const grouped: Record<WorkspaceCategory, WorkspaceDefinition[]> = {
    'territorial-core': [],
    'decision-intelligence': [],
    'operational-management': [],
  };

  for (const ws of WORKSPACE_REGISTRY) {
    grouped[ws.category].push(ws);
  }

  return grouped;
}
